import React, { useState, useMemo } from 'react';
import { matchesCanonicalSearch } from '../../shared/searchFilter';
import { getFieldDisplayLabel } from '../../shared/searchFields';

/* ── Status badge config ───────────────────────────────────────────────── */
function statusClass(status) {
  const s = (status || '').toUpperCase();
  if (s === 'ACTIVE') return 'ar-status-active';
  if (s === 'LOW MVC') return 'ar-status-lowmvc';
  if (['OBSOLETE', 'DISCON', 'SCRAPPED'].includes(s)) return 'ar-status-danger';
  return 'ar-status-tbd';
}

function statusLabel(status) {
  return (status || 'TBD').toUpperCase();
}

/* ── Match context (search highlight) ──────────────────────────────────── */
const HIGHLIGHT_STYLE = { color: 'var(--color-danger)', fontWeight: 'var(--font-semibold)' };

/* S1's intentional divergence (standing memory), now data-driven:
   - skip supplier_tool_id — it IS the row header column, a badge is redundant
   - relabel preliminary_tool_status as the shorter "Status"
   Every other canonical field routes through the shared FIELD_DISPLAY_LABELS
   via getFieldDisplayLabel. Session E made search_inventory annotate
   _match_context for ALL canonical fields, so this is now purely a
   label-resolution + divergence concern (no client field-loop needed —
   S1 only shows the badge at >=2 chars over server results that always
   carry _match_context). */
const FIELD_OVERRIDES_S1 = { preliminary_tool_status: 'Status' };
const FIELDS_SKIPPED_S1 = new Set(['supplier_tool_id']);

// Exported (named, additive — zero behavior change) so Session E's logic
// test can exercise S1's divergence directly. The surface still uses it
// internally as `computeMatchContext`.
export function computeMatchContext(tool, searchTerm) {
  if (!searchTerm || searchTerm.length < 2) return null;
  const mc = tool && tool._match_context;
  if (!mc || !mc.field || mc.value == null || String(mc.value).trim() === '') return null;
  if (FIELDS_SKIPPED_S1.has(mc.field)) return null;
  const label = FIELD_OVERRIDES_S1[mc.field] || getFieldDisplayLabel(mc.field);
  return { label, value: String(mc.value) };
}

function renderHighlightedValue(value, searchTerm) {
  if (!searchTerm || !value) return value;
  const q = searchTerm.toLowerCase();
  const idx = value.toLowerCase().indexOf(q);
  if (idx === -1) return value;

  let pre = value.substring(0, idx);
  const match = value.substring(idx, idx + searchTerm.length);
  let post = value.substring(idx + searchTerm.length);

  // Truncate long values centered on match
  if (value.length > 30) {
    const start = Math.max(0, idx - 15);
    const end = Math.min(value.length, idx + searchTerm.length + 15);
    pre = (start > 0 ? '\u2026' : '') + value.substring(start, idx);
    post = value.substring(idx + searchTerm.length, end) + (end < value.length ? '\u2026' : '');
  }

  return <>{pre}<span style={HIGHLIGHT_STYLE}>{match}</span>{post}</>;
}

/* ── Search matching ───────────────────────────────────────────────────── */
/* The bespoke per-surface predicate was replaced by the shared canonical
   filter (src/shared/searchFilter.js) so all surfaces match the same 27-field
   set. Asset Registry tools are raw snake_case → no fieldNameMap needed. */

/* ── Columns ───────────────────────────────────────────────────────────── */
const COLUMNS = [
  { key: 'supplier_tool_id', header: 'Tool ID' },
  { key: 'alt_tool_id', header: 'Alt Tool ID' },
  { key: 'tool_type', header: 'Tool Type' },
  { key: 'material', header: 'Material' },
  { key: 'oem', header: 'OEM' },
  { key: 'site_location', header: 'Site Location' },
  { key: 'detail_location', header: 'Detail Location' },
  { key: 'preliminary_tool_status', header: 'Prelim Status' },
];

const PRELIM_STATUSES = ['ACTIVE', 'DISCON', 'LOW MVC', 'OBSOLETE', 'SCRAPPED', 'TBD'];

/* ══════════════════════════════════════════════════════════════════════════ */

export default function AssetToolList({
  tools, searchResults = null, loading, error, selectedSite, selectedTool,
  onSelectTool, searchQuery, onSearchChange, siteName,
}) {
  const [sortCol, setSortCol] = useState(null);
  const [sortDir, setSortDir] = useState(null); // 'asc' | 'desc' | null
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({ tool_type: '', material: '', oem: '', site_location: '', preliminary_tool_status: '' });

  /* ── Filter pipeline ─────────────────────────────────────────────────── */
  const visibleTools = useMemo(() => {
    // searchResults (server canonical search, query >= MIN) is authoritative —
    // displayed as-is (dropdown filters + sort still apply). null means
    // short/no query → filter the full loaded list with the shared gate.
    const usingServer = searchResults !== null;
    let result = usingServer ? [...searchResults] : [...tools];

    if (!usingServer) {
      result = result.filter(t => matchesCanonicalSearch(t, searchQuery));
    }

    // Filters (AND logic)
    if (filters.tool_type) result = result.filter(t => t.tool_type === filters.tool_type);
    if (filters.material) result = result.filter(t => t.material === filters.material);
    if (filters.oem) result = result.filter(t => t.oem === filters.oem);
    if (filters.site_location) result = result.filter(t => t.site_location === filters.site_location);
    if (filters.preliminary_tool_status) {
      result = result.filter(t => {
        const s = (t.preliminary_tool_status || 'TBD').toUpperCase();
        return s === filters.preliminary_tool_status;
      });
    }

    // Sort
    if (sortCol && sortDir) {
      result.sort((a, b) => {
        const av = (a[sortCol] || '').toString().toLowerCase();
        const bv = (b[sortCol] || '').toString().toLowerCase();
        if (av < bv) return sortDir === 'asc' ? -1 : 1;
        if (av > bv) return sortDir === 'asc' ? 1 : -1;
        return 0;
      });
    }

    return result;
  }, [tools, searchResults, searchQuery, filters, sortCol, sortDir]);

  /* ── Filter value options (dynamic from dataset) ─────────────────────── */
  const filterOptions = useMemo(() => ({
    tool_type: [...new Set(tools.map(t => t.tool_type).filter(Boolean))].sort(),
    material: [...new Set(tools.map(t => t.material).filter(Boolean))].sort(),
    oem: [...new Set(tools.map(t => t.oem).filter(Boolean))].sort(),
    site_location: [...new Set(tools.map(t => t.site_location).filter(Boolean))].sort(),
  }), [tools]);

  const activeFilterCount = Object.values(filters).filter(Boolean).length;

  /* ── Sort handler ────────────────────────────────────────────────────── */
  function handleSort(col) {
    if (sortCol !== col) { setSortCol(col); setSortDir('asc'); }
    else if (sortDir === 'asc') setSortDir('desc');
    else { setSortCol(null); setSortDir(null); }
  }

  /* ── Export ──────────────────────────────────────────────────────────── */
  async function handleExport() {
    const XLSX = await import('xlsx');
    const rows = visibleTools.map(t => ({
      'Tool ID': t.supplier_tool_id || '',
      'Alt Tool ID': t.alt_tool_id || '',
      'Tool Type': t.tool_type || '',
      'Material': t.material || '',
      'OEM': t.oem || '',
      'Site Location': t.site_location || '',
      'Detail Location': t.detail_location || '',
      'Prelim Status': (t.preliminary_tool_status || 'TBD').toUpperCase(),
    }));
    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'ToolSight Export');
    const date = new Date().toISOString().split('T')[0];
    XLSX.writeFile(wb, `ToolSight_Asset_Registry_${siteName.replace(/[^a-zA-Z0-9]/g, '_')}_${date}.xlsx`);
  }

  function clearFilters() {
    setFilters({ tool_type: '', material: '', oem: '', site_location: '', preliminary_tool_status: '' });
  }

  /* ── Empty states ────────────────────────────────────────────────────── */
  if (!selectedSite) return <div className="ar-empty">Select a site to view tools</div>;
  if (loading) return <div className="ar-empty"><div className="ar-spinner" /></div>;
  if (error) return <div className="ar-empty" style={{ color: 'var(--color-danger)' }}>{error}</div>;

  return (
    <>
      {/* Toolbar */}
      <div className="ar-toolbar">
        <div className="ar-search-wrap">
          <input
            type="text"
            className="ar-search-input"
            placeholder="Search Tool ID, Asset Tag, Part Number…"
            value={searchQuery}
            onChange={e => onSearchChange(e.target.value)}
          />
          {searchQuery && (
            <button
              className="ar-search-clear"
              onClick={() => onSearchChange('')}
              type="button"
              aria-label="Clear search"
            >
              ×
            </button>
          )}
        </div>
        {activeFilterCount > 0 && (
          <span className="ar-filter-badge" onClick={() => setShowFilters(!showFilters)}>
            {activeFilterCount} filter{activeFilterCount > 1 ? 's' : ''} active
          </span>
        )}
        <button
          className="ar-filter-badge"
          style={activeFilterCount === 0 ? { background: 'transparent', border: '1px solid var(--color-border-input)', color: 'var(--color-text-muted)' } : undefined}
          onClick={() => setShowFilters(!showFilters)}
        >
          Filters
        </button>
        <div className="ar-toolbar-spacer" />
        <button className="ar-export-btn" onClick={handleExport}>Export to Excel</button>
      </div>

      {/* Filter bar */}
      {showFilters && (
        <div className="ar-filters">
          <div>
            <span className="ar-filter-label">Tool Type</span>
            <select className="ar-filter-select" value={filters.tool_type} onChange={e => setFilters(p => ({ ...p, tool_type: e.target.value }))}>
              <option value="">All</option>
              {filterOptions.tool_type.map(v => <option key={v} value={v}>{v}</option>)}
            </select>
          </div>
          <div>
            <span className="ar-filter-label">Material</span>
            <select className="ar-filter-select" value={filters.material} onChange={e => setFilters(p => ({ ...p, material: e.target.value }))}>
              <option value="">All</option>
              {filterOptions.material.map(v => <option key={v} value={v}>{v}</option>)}
            </select>
          </div>
          <div>
            <span className="ar-filter-label">OEM</span>
            <select className="ar-filter-select" value={filters.oem} onChange={e => setFilters(p => ({ ...p, oem: e.target.value }))}>
              <option value="">All</option>
              {filterOptions.oem.map(v => <option key={v} value={v}>{v}</option>)}
            </select>
          </div>
          <div>
            <span className="ar-filter-label">Site Location</span>
            <select className="ar-filter-select" value={filters.site_location} onChange={e => setFilters(p => ({ ...p, site_location: e.target.value }))}>
              <option value="">All</option>
              {filterOptions.site_location.map(v => <option key={v} value={v}>{v}</option>)}
            </select>
          </div>
          <div>
            <span className="ar-filter-label">Prelim Status</span>
            <select className="ar-filter-select" value={filters.preliminary_tool_status} onChange={e => setFilters(p => ({ ...p, preliminary_tool_status: e.target.value }))}>
              <option value="">All</option>
              {PRELIM_STATUSES.map(v => <option key={v} value={v}>{v}</option>)}
            </select>
          </div>
          <button className="ar-filter-clear" onClick={clearFilters}>Clear All</button>
        </div>
      )}

      {/* Tool count */}
      {tools.length > 0 && (
        <div className="ar-tool-count" style={{ padding: 'var(--space-2) var(--space-8) var(--space-4)', fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)' }}>
          {(activeFilterCount > 0 || (searchQuery && searchQuery.length >= 2))
            ? `Displaying ${visibleTools.length} of ${tools.length} tools`
            : `Displaying ${tools.length} tools`}
        </div>
      )}

      {/* Table */}
      {tools.length === 0 ? (
        <div className="ar-empty">No tools found at this site</div>
      ) : visibleTools.length === 0 ? (
        <div className="ar-empty">
          No tools match the current filters.{' '}
          <button className="ar-filter-clear" onClick={() => { clearFilters(); onSearchChange(''); }}>Clear filters</button>
        </div>
      ) : (
        <div className="ar-table-wrap">
          <table className="ar-table">
            <thead>
              <tr>
                {COLUMNS.map(col => (
                  <th key={col.key} onClick={() => handleSort(col.key)}>
                    {col.header}
                    {sortCol === col.key && (
                      <span className="ar-sort-arrow">{sortDir === 'asc' ? '↑' : '↓'}</span>
                    )}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {visibleTools.map(tool => {
                const tid = tool.id || tool.tool_id;
                const isSelected = selectedTool && (selectedTool.id || selectedTool.tool_id) === tid;
                const matchCtx = searchQuery && searchQuery.length >= 2 ? computeMatchContext(tool, searchQuery) : null;

                return (
                  <tr
                    key={tid}
                    className={isSelected ? 'ar-row-selected' : ''}
                    onClick={() => onSelectTool(tool)}
                  >
                    <td>
                      <div className="ar-cell-main">
                        {(searchQuery && searchQuery.length >= 2 && tool.supplier_tool_id)
                          ? renderHighlightedValue(tool.supplier_tool_id, searchQuery)
                          : (tool.supplier_tool_id || '—')}
                      </div>
                      {matchCtx && (
                        <div className="ar-match-context">
                          {matchCtx.label}: {renderHighlightedValue(matchCtx.value, searchQuery)}
                        </div>
                      )}
                    </td>
                    <td>{tool.alt_tool_id || '—'}</td>
                    <td>{tool.tool_type || '—'}</td>
                    <td>{tool.material || '—'}</td>
                    <td>{tool.oem || '—'}</td>
                    <td>{tool.site_location || '—'}</td>
                    <td>{tool.detail_location || '—'}</td>
                    <td>
                      <span className={`ar-status-badge ${statusClass(tool.preliminary_tool_status)}`}>
                        {statusLabel(tool.preliminary_tool_status)}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}
